import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../home/components/Footer';
import XFollowWidget from '../../components/feature/XFollowWidget';
import { blogPosts } from '../../mocks/blog-posts';
import { useSEO } from '../../hooks/useSEO';

const CATEGORIES = ['All', 'Entrepreneurs', 'Business', 'Writing', 'Image', 'Video', 'News', 'Marketing'];

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${monthNames[month - 1]} ${day}, ${year}`;
}

function getLatestPost() {
  return [...blogPosts].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  })[0];
}

interface BlogPost {
  id: number;
  slug: string;
  title: string;
  description: string;
  category: string;
  image: string;
  date: string;
  readTime: string;
  author: string;
  avatar: string;
}

function FeaturedPost({ post }: { post: BlogPost }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group relative block w-full rounded-2xl overflow-hidden cursor-pointer"
      style={{ minHeight: '420px' }}
    >
      <img
        src={post.image}
        alt={post.title}
        className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/10" />
      <div className="absolute top-5 left-5">
        <span className="px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full uppercase tracking-wide">
          Latest Post
        </span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-8">
        <span className="inline-block px-3 py-1 bg-white/10 text-teal-400 text-xs font-medium rounded-full mb-3 border border-teal-500/30">
          {post.category}
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 group-hover:text-teal-300 transition-colors duration-200 leading-tight">
          {post.title}
        </h2>
        <p className="text-gray-300 text-base mb-5 max-w-3xl line-clamp-2">
          {post.description}
        </p>
        <div className="flex items-center gap-4">
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-teal-500 flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/81ddcca4e455aa4e3e9404d7fe6eefde/e7fd262c64409f252189cfe0fbf7a092.png"
              alt="ToolNova"
              className="w-5 h-5 object-contain"
            />
          </div>
          <div>
            <p className="text-white text-sm font-medium">{post.author}</p>
            <p className="text-gray-400 text-xs">{formatDate(post.date)} &middot; {post.readTime} read</p>
          </div>
          <span className="ml-auto text-teal-400 font-medium text-sm group-hover:translate-x-1 transition-transform duration-200 whitespace-nowrap">
            Read Article &rarr;
          </span>
        </div>
      </div>
    </Link>
  );
}

function BlogCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex flex-col rounded-xl overflow-hidden cursor-pointer border border-white/5 hover:border-teal-500/30 transition-all duration-300"
      style={{ backgroundColor: '#111827' }}
    >
      <div className="relative h-48 overflow-hidden flex-shrink-0">
        <img
          src={post.image}
          alt={post.title}
          loading="lazy"
          className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 bg-teal-600/90 text-white text-xs font-semibold rounded-full">
            {post.category}
          </span>
        </div>
      </div>
      <div className="flex flex-col flex-1 p-5">
        <h3 className="text-base font-bold text-white mb-2 group-hover:text-teal-400 transition-colors duration-200 line-clamp-2 leading-snug">
          {post.title}
        </h3>
        <p className="text-gray-400 text-sm mb-4 line-clamp-2 leading-relaxed flex-1">
          {post.description}
        </p>
        <div className="flex items-center gap-3 mt-auto pt-3 border-t border-white/5">
          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-teal-500 flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/81ddcca4e455aa4e3e9404d7fe6eefde/e7fd262c64409f252189cfe0fbf7a092.png"
              alt="ToolNova"
              loading="lazy"
              className="w-4 h-4 object-contain"
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-white text-xs font-medium truncate">{post.author}</p>
            <p className="text-gray-500 text-xs">{formatDate(post.date)}</p>
          </div>
          <span className="text-gray-500 text-xs whitespace-nowrap flex-shrink-0">{post.readTime}</span>
        </div>
      </div>
    </Link>
  );
}

export default function Blog() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://toolnova.ai';
  const [activeCategory, setActiveCategory] = useState('All');

  useSEO({
    title: 'AI Tools Blog - Tips, Reviews & Guides | ToolNova',
    description: 'Explore expert guides, top AI tool reviews, and category breakdowns to help you find the right AI tools for your workflow.',
    keywords: 'AI tools blog, AI tool reviews, AI guides, AI tips, artificial intelligence articles',
    canonical: `${siteUrl}/blog`,
    ogTitle: 'AI Tools Blog - Tips, Reviews & Guides | ToolNova',
    ogDescription: 'Explore expert guides, top AI tool reviews, and category breakdowns to help you find the right AI tools for your workflow.',
    ogType: 'website',
    ogUrl: `${siteUrl}/blog`,
    ogImage: 'https://public.readdy.ai/ai/img_res/a69a1da68a635dbd01251f9683cce750.jpg',
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'ToolNova Blog',
        description: 'Expert guides, reviews, and insights on AI tools for creators and businesses.',
        url: `${siteUrl}/blog`,
        publisher: {
          '@type': 'Organization',
          name: 'ToolNova',
          url: siteUrl,
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/81ddcca4e455aa4e3e9404d7fe6eefde/e7fd262c64409f252189cfe0fbf7a092.png',
          },
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'ToolNova Blog Articles',
        url: `${siteUrl}/blog`,
        itemListElement: blogPosts.slice(0, 6).map((post, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `${siteUrl}/blog/${post.slug}`,
          name: post.title,
        })),
      },
    ],
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const latestPost = getLatestPost();

  const filteredPosts = activeCategory === 'All'
    ? blogPosts.filter((p) => p.id !== latestPost.id)
    : blogPosts.filter((p) => p.category === activeCategory && p.id !== latestPost.id);

  const showLatestInGrid = activeCategory !== 'All';

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }}>
      <Navbar />

      {/* Page Hero */}
      <div className="pt-28 pb-10 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
            AI Tools Insights &amp; Guides
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
            Expert reviews, how-to guides, and the latest news on AI tools to help you work smarter and faster.
          </p>
        </div>
      </div>

      <div className="px-6 pb-20">
        <div className="max-w-6xl mx-auto">

          {/* Featured Latest Post */}
          {activeCategory === 'All' && (
            <div className="mb-12">
              <FeaturedPost post={latestPost} />
            </div>
          )}

          {/* Category Filter */}
          <div className="flex items-center gap-2 flex-wrap mb-8">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-teal-500 text-white'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
              >
                {cat}
                <span className="ml-1.5 text-xs opacity-60">
                  {cat === 'All'
                    ? blogPosts.length
                    : blogPosts.filter((p) => p.category === cat).length}
                </span>
              </button>
            ))}
          </div>

          {/* X Follow Widget */}
          <div className="mb-8">
            <XFollowWidget variant="banner" />
          </div>

          {/* When filtering and the latest post matches, show it too */}
          {showLatestInGrid && latestPost.category === activeCategory && (
            <div className="mb-8">
              <FeaturedPost post={latestPost} />
            </div>
          )}

          {/* Results info */}
          <p className="text-gray-500 text-sm mb-6">
            Showing{' '}
            <span className="text-white font-medium">
              {showLatestInGrid
                ? blogPosts.filter((p) => p.category === activeCategory).length
                : filteredPosts.length + 1}
            </span>{' '}
            article{(showLatestInGrid
              ? blogPosts.filter((p) => p.category === activeCategory).length
              : filteredPosts.length + 1) !== 1
              ? 's'
              : ''}
            {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
          </p>

          {/* Blog Grid */}
          {filteredPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-file-search-line text-5xl text-gray-600 mb-4 block" />
              <p className="text-gray-400 text-lg">No articles in this category yet.</p>
              <button
                onClick={() => setActiveCategory('All')}
                className="mt-4 px-5 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium cursor-pointer hover:bg-teal-600 transition-colors whitespace-nowrap"
              >
                View All Articles
              </button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
